/**
 * reRankService — frontend wrapper around
 * POST /api/v1/chat/conversations/{publicId}/re-rank (the Slice A5 weight-slider
 * re-rank endpoint). Deterministic, non-LLM: rebuilds the ranking from the
 * conversation's stored candidate set + constraints with adjusted scoring
 * weights. Never consumes chat quota.
 *
 * Returns parsed JSON on success (a `ranked_cells_table` artifact payload,
 * optionally carrying a `selection_context` blob — see
 * chatStore.applyReRankResult) and throws on non-2xx.
 */
import { authenticatedFetch } from './authService.js'
import { getApiBase } from '../components/admin/analytics/shared/formatters.js'

const BASE = (publicId) =>
  `${getApiBase()}/chat/conversations/${encodeURIComponent(publicId)}/re-rank`

async function jsonOrThrow(res) {
  if (res.ok) return await res.json()
  let detail = `HTTP ${res.status}`
  try {
    const b = await res.json()
    // FastAPI `detail` is often an object ({code, message}) for our structured
    // errors — coerce to a readable string so callers never surface
    // "[object Object]" to the user.
    if (b?.detail && typeof b.detail === 'object') {
      detail = b.detail.message || JSON.stringify(b.detail)
    } else if (b?.detail) {
      detail = b.detail
    }
  } catch {}
  throw new Error(detail)
}

/**
 * Deterministic re-rank. Send scoring weights, hard-constraint overrides, or
 * both — the endpoint requires at least one.
 *
 * @param {string} publicId
 * @param {object} [opts]
 * @param {Record<string, number>} [opts.scoringWeights] — full weights map.
 * @param {Record<string, object|Array|null>} [opts.hardConstraints] — per-key
 *   overrides: a ConstraintSpec confirms/edits the constraint (backend marks it
 *   `user_confirmed`); `null` removes it.
 *
 * Body is snake_case: `{ scoring_weights?, hard_constraints? }`. A weights-only
 * (or argument-less) call always sends `scoring_weights` — even `{}`, which the
 * endpoint reads as "keep the current weights" — so the slider path is
 * unchanged. A constraints-only call omits `scoring_weights` entirely.
 */
export async function reRank(publicId, { scoringWeights, hardConstraints } = {}) {
  const body = {}
  if (hardConstraints !== undefined) body.hard_constraints = hardConstraints
  if (scoringWeights !== undefined || hardConstraints === undefined) {
    body.scoring_weights = scoringWeights || {}
  }
  return jsonOrThrow(await authenticatedFetch(BASE(publicId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }))
}
